import { handleFetchInboundOrders } from "api/fetchers";
import { AddIcon, ArrowDown, CloseIcon, DocsFilledIcon, SearchIcon } from "asset/icons";
import { currencyToDisplay, formatDate } from "component/flex/utils";
import Input from "component/form/Input";
import useDebounce from "hooks/useDebounce";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Bars } from "react-loader-spinner";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { formatMoney } from "utilities/formatMoney";
import { useNavigate } from "react-router-dom";
import { CheckIcon } from "lucide-react";
import { CRYPTO_MINIMUM_AMOUNT, MINIMUM_AMOUNT } from "constants/FinanceConstants";
import { isCrypto } from "constants/global";

const PurchaseOrdersSelect = ({ setOrderDimensionsList, forFinance = false, forRepayment }) => {
    const [openModal, setOpenModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [search, setSearch] = useState("");
    const { currency } = useSelector((state) => state);
    const navigate = useNavigate();
    const handleSearch = useDebounce((term) => {
        setSearch(term);
    }, 700);
    const { data, isLoading } = useQuery({
        queryKey: ["get-completed-orders", search, currency],
        queryFn: async () =>
            handleFetchInboundOrders({
                searchTerm: search,
                currency,
                sortBy: "newest-first",
                forFinance:forRepayment,
            }),
    });


    const confirmedPO = data?.inbound?.filter(
        (order) =>
            order.orderStatus === "PENDING" ||
            order.orderStatus === "CONFIRMED" ||
            order.orderStatus === "COMPLETED"
    );
    const confirmedPOForRepayment = data?.outBound?.filter(
        (order) =>
            order.orderStatus === "PENDING" ||
            order.orderStatus === "CONFIRMED"
    );
  const MINIMUM_AMOUNT_TO_USE = isCrypto(currency) ? CRYPTO_MINIMUM_AMOUNT : MINIMUM_AMOUNT;
    const confirmedPOForFinance = data?.inbound?.filter(
      (order) => (order.orderStatus === "PENDING" || order.orderStatus === "CONFIRMED") && order?.totalAmount >= MINIMUM_AMOUNT_TO_USE
    );
    const poToRender = forRepayment
        ? confirmedPOForRepayment
        : forFinance
        ? confirmedPOForFinance
        : confirmedPO;

    useEffect(() => {
        handleSearch(searchTerm);
    }, [searchTerm]);

    const handleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
        );
    };

    const clearSelection = () => setSelectedIds([]);
    return (
        <>
            {forFinance ? (
                <p
                    onClick={() => setOpenModal(true)}
                    className="h-[56px] w-full bg-secondary-30 flex items-center justify-between rounded-md p-3 cursor-pointer"
                >
                    <span className="text-sm font-light">Select a purchase order</span>

                    <ArrowDown />
                </p>
            ) : (
                <p
                    className="flex items-center !px-3.5 h-16 bg-white rounded-lg text-main-dark cursor-pointer"
                    onClick={() => setOpenModal(true)}
                >
                    <DocsFilledIcon className="!mr-2" />
                    Select Purchase Order
                    <AddIcon className="ml-auto" />
                </p>
            )}

            <Modal show={openModal} centered onHide={() => setOpenModal(false)}>
                <div
                    style={{ maxHeight: "85vh" }}
                    className="rounded-2xl text-main-dark overflow-hidden bg-[#F5F4FA]"
                >
                    {" "}
                    <h3 className="flex justify-between items-center !py-4 !px-6 font-semibold text-xl">
                        Choose purchase orders{" "}
                        <CloseIcon
                            onClick={() => setOpenModal((p) => !p)}
                            className="size-[25px] cursor-pointer rounded-full *:stroke-main-dark"
                        />
                    </h3>
                    <div className="!px-6 mb-2">
                        <Input
                            onChange={(value) => {
                                setSearchTerm(value);
                            }}
                            value={searchTerm}
                            placeholder="Search by item, email or customer"
                            unit={<SearchIcon className="-order-1 !ml-4 !size-6" />}
                            className="!shadow-[inset_0_0_0_1000px_#EAECF0] !pl-3 placeholder:text-base placeholder:font-normal"
                        />
                    </div>
                    {selectedIds.length > 0 && (
                        <div className="px-4 mt-1 mb-1 text-sm">
                            <div className="flex justify-between">
                                <p className="text-gray-400">"{selectedIds.length}" selected</p>
                                <button onClick={clearSelection} className="text-lg font-bold">
                                    Clear selection
                                </button>
                            </div>
                        </div>
                    )}
                    {/* Transactions List */}
                    {isLoading ? (
                        <Bars height="20" width="20" color="#fff" ariaLabel="bars-loading" />
                    ) : (
                        <div className="p-4 space-y-3 overflow-y-auto h-[50vh]">
                            {poToRender?.length ? (
                                poToRender?.map((transaction, index) => {
                                    const id = `${transaction?._id}`;
                                    const isSelected = selectedIds.includes(id);

                                    return (
                                        <div
                                            key={id}
                                            onClick={() =>
                                                forFinance
                                                    ? setOrderDimensionsList(transaction)
                                                    : handleSelect(id)
                                            }
                                            className="flex items-center justify-between px-2 py-2 bg-white rounded-lg"
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="">
                                                    <div
                                                        style={{
                                                            border: isSelected
                                                                ? ""
                                                                : "1px solid #D0D5DD",
                                                            background: isSelected
                                                                ? "#6C47FF"
                                                                : "white",
                                                        }}
                                                        onClick={() =>
                                                            forFinance
                                                                ? setOrderDimensionsList(
                                                                      transaction
                                                                  )
                                                                : handleSelect(id)
                                                        }
                                                        className="flex items-center justify-center rounded-full size-5"
                                                    >
                                                        {isSelected && (
                                                            <CheckIcon size="14" color="white" />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <div className="text-sm font-medium text-[#111827]">
                                                        {transaction?.title} •{" "}
                                                        {transaction?.outbound?.businessName}
                                                    </div>
                                                    <div className="flex gap-2 mt-0 text-sm">
                                                        <span className="text-[#22C55E] font-medium">
                                                            {transaction.orderStatus}
                                                        </span>
                                                        <span className="text-[#9CA3AF]">•</span>
                                                        <span className="text-[#6B7280]">
                                                            {formatDate(transaction.createdAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-medium text-[#111827]">
                                            {currencyToDisplay(currency, transaction?.totalAmount)}{" "}
                                                </div>
                                                <div className="text-xs text-[#6B7280]">
                                                    {transaction?.orderItems?.length} Items
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : forFinance ? (
                                <div className="flex flex-col items-center">
                                    <p className="px-3 py-2 font-light text-center text-gray-700">
                                        You don't have any outbound purchase orders.  <br />{" "}
                                    </p>
                                    <button
                                        onClick={() => {
                                            navigate("/flex/create-po");
                                        }}
                                        className="!text-secondary text-center mx-auto"
                                    >
                                        Create one now.
                                    </button>
                                </div>
                            ) : (
                                <p className="px-3 py-2 font-light text-center text-gray-700">
                                    You don't have any order yet
                                </p>
                            )}
                        </div>
                    )}
                    <div className="flex justify-end gap-2 px-4 py-2 text-xs bg-white border-t rounded-sm border-t-gray-200">
                        <button
                            onClick={() => {
                                if (!forFinance) {
                                    setOpenModal(false);
                                    setOrderDimensionsList([]);
                                } else {
                                    setOpenModal(false);
                                }
                            }}
                            className="px-4 py-2 text-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                if (!forFinance) {
                                    setOrderDimensionsList(() =>
                                        poToRender?.filter((item) =>
                                            selectedIds.includes(item?._id)
                                        )
                                    );
                                    setOpenModal(false);
                                } else {
                                    setOpenModal(false);
                                }
                            }}
                            className="bg-[#6F00FF] text-white px-3 py-1 rounded-md text-md"
                        >
                            Add Purchase Orders
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default PurchaseOrdersSelect;
